import * as React from "react";

import { Container, Box, Typography, Link as MUILink, List, ListItem, Divider, Chip, Stack, Breadcrumbs } from "@mui/material";

const Section = ({ id, title, children }) => (
  <Box component="section" id={id} sx={{ scrollMarginTop: { xs: 80, md: 100 } }}>
    <Typography variant="h5" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    <Box sx={{ color: "text.secondary" }}>{children}</Box>
  </Box>
);

export default function TermsOfServicePage() {
  const updated = React.useMemo(() => {
    return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "long", day: "2-digit" }).format(new Date("2025-09-24"));
  }, []);

  const anchor = (href, label) => (
    <MUILink href={href} underline="hover" color="inherit">
      {label}
    </MUILink>
  );

  return (
    <>


      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Chip label="Terms" size="small" />
          <Typography variant="overline" color="text.secondary">
            Last updated: {updated}
          </Typography>
        </Stack>

        <Typography variant="h3" fontWeight={800} gutterBottom>
          Terms of Service
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Welcome to ReportWerks. By using our website, services, and integrations, you agree to the following Terms of Service. Please read them carefully. If you do not agree, you may not use our services.
        </Typography>

        <Breadcrumbs aria-label="terms sections" sx={{ mb: 4 }}>
          {anchor("#eligibility", "Eligibility")}
          {anchor("#account", "Account Responsibilities")}
          {anchor("#use", "Use of Services")}
          {anchor("#oauth", "OAuth & Connected Accounts")}
          {anchor("#data", "Data & Content")}
          {anchor("#availability", "Service Availability")}
          {anchor("#fees", "Fees & Payment")}
          {anchor("#disclaimer", "Disclaimer of Warranties")}
          {anchor("#liability", "Limitation of Liability")}
          {anchor("#termination", "Termination")}
          {anchor("#law", "Governing Law")}
          {anchor("#changes", "Changes to Terms")}
          {anchor("#contact", "Contact Us")}
        </Breadcrumbs>

        <Section id="eligibility" title="1. Eligibility">
          <Typography>You must be at least 18 years old and legally capable of entering into contracts to use ReportWerks.</Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="account" title="2. Account Responsibilities">
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>You are responsible for safeguarding your login credentials.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Do not share your account with unauthorized individuals.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>You are responsible for all activity under your account.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="use" title="3. Use of Services">
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>ReportWerks provides analytics and reporting dashboards for nonprofits, agencies, and related organizations.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>You agree to use the service only for lawful purposes in accordance with applicable laws.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>You may not attempt to disrupt, overload, or reverse engineer our systems.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="oauth" title="4. OAuth & Connected Accounts">
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>When you connect third-party platforms, you authorize ReportWerks to use OAuth to obtain access tokens.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Tokens are used solely to fetch and display reporting data in your dashboards.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>You may revoke access anytime via the provider’s security settings or within ReportWerks.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Revoking access may limit functionality.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="data" title="5. Data & Content">
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>You retain ownership of your data.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>By using ReportWerks, you grant us a limited license to process and display your data as needed for the service.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>We may store aggregated, anonymized usage statistics for improvement.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="availability" title="6. Service Availability">
          <Typography>We strive for high availability but do not guarantee uninterrupted or error-free service. Planned maintenance or outages may temporarily limit access.</Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="fees" title="7. Fees & Payment">
          <Typography>Some features may require a paid subscription. Fees, payment terms, and renewal conditions are provided at the time of subscription. Failure to pay may result in suspension or termination.</Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="disclaimer" title="8. Disclaimer of Warranties">
          <Typography>ReportWerks is provided “as is” and “as available” without warranties of any kind, either express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="liability" title="9. Limitation of Liability">
          <Typography>To the maximum extent permitted by law, ReportWerks and its affiliates will not be liable for indirect, incidental, special, consequential, or punitive damages. Our total liability will not exceed the fees paid in the 12 months preceding a claim.</Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="termination" title="10. Termination">
          <Typography>We may suspend or terminate your account if you violate these Terms. Upon termination, you lose access to the service and associated data, subject to retention and deletion policies.</Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="law" title="11. Governing Law">
          <Typography>These Terms are governed by the laws of the State of Georgia, USA. Disputes will be resolved in the courts located in Cherokee County, Georgia.</Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="changes" title="12. Changes to Terms">
          <Typography>We may update these Terms periodically. Continued use of ReportWerks after changes constitutes acceptance of the updated Terms.</Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="contact" title="13. Contact Us">
          <Typography sx={{ mb: 1 }}>If you have questions about these Terms, please contact us:</Typography>
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}><strong>ReportWerks</strong></ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Email: <MUILink href="mailto:support@reportwerks.com">support@reportwerks.com</MUILink></ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Website: <MUILink href="https://www.reportwerks.com" target="_blank" rel="noopener">www.reportwerks.com</MUILink></ListItem>
          </List>
        </Section>
      </Container>
    </>
  );
}